import { supabase } from './supabase';
import type { Database } from './supabase';
import { logger } from './logger';

type UserSubscription = Database['public']['Tables']['user_subscriptions']['Row'];
type SubscriptionPlan = Database['public']['Tables']['subscription_plans']['Row'];
type SubscriptionUsage = Database['public']['Tables']['subscription_usage']['Row'];
type SubscriptionInvoice = Database['public']['Tables']['subscription_invoices']['Row'];
type UserSubscriptionWithPlan = UserSubscription & { subscription_plans: SubscriptionPlan | null };

export interface SubscriptionWithPlan {
  id: string;
  user_id: string;
  plan_id: string;
  status: 'active' | 'cancelled' | 'expired' | 'past_due' | 'trial';
  billing_cycle: 'monthly' | 'annual';
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  scheduled_cancellation_date: string | null;
  cancelled_at: string | null;
  trial_start: string | null;
  trial_end: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  created_at: string;
  updated_at: string;
  subscription_plans: SubscriptionPlan;
}

export interface SubscriptionUsageWithDetails extends SubscriptionUsage {
  subscription: UserSubscription;
  plan: SubscriptionPlan;
}

/**
 * Fetches all active subscriptions for a user with plan details
 * @param userId - The user ID to fetch subscriptions for
 * @returns Array of subscriptions with plan details
 */
export async function getAllActiveSubscriptions(userId: string): Promise<SubscriptionWithPlan[]> {
  const now = new Date().toISOString();

  const { data, error } = await supabase
    .from('user_subscriptions')
    .select(`
      *,
      subscription_plans (*)
    `)
    .eq('user_id', userId)
    .in('status', ['active', 'trial', 'past_due']) // Include past_due for graceful SaaS access
    .gte('current_period_end', now)
    .order('created_at', { ascending: false });

  if (error) {
    logger.error('Error fetching subscriptions:', error);
    return [];
  }

  // Safety check: filter out cancelled/expired just in case
  return (data || []).filter(sub => sub.status !== 'cancelled' && sub.status !== 'expired') as SubscriptionWithPlan[];
}

/**
 * Fetches the active subscription for a user with plan details
 * Returns the most recent active subscription if multiple exist
 * @param userId - The user ID to fetch subscription for
 * @returns Subscription with plan details, or null if no active subscription
 */
export async function getActiveSubscription(userId: string): Promise<SubscriptionWithPlan | null> {
  const subscriptions = await getAllActiveSubscriptions(userId);
  return subscriptions.length > 0 ? subscriptions[0] : null;
}

/**
 * Fetches the current subscription usage (credits remaining) for the active subscription
 * If no subscriptionId is provided, aggregates credits from ALL active subscriptions
 * @param userId - The user ID to fetch usage for
 * @param subscriptionId - Optional subscription ID. If provided, returns usage ONLY for that subscription.
 * @returns Current usage details or null if no active subscription/usage found
 */
export async function getCurrentSubscriptionUsage(
  userId: string,
  subscriptionId?: string
): Promise<SubscriptionUsageWithDetails | null> {
  const now = new Date().toISOString();

  // If a specific subscription ID is requested, fetch just that one
  if (subscriptionId) {
    const { data, error } = await supabase
      .from('subscription_usage')
      .select('*')
      .eq('user_id', userId)
      .eq('subscription_id', subscriptionId)
      .lte('period_start', now)
      .gt('period_end', now)
      .order('period_start', { ascending: false })
      .maybeSingle();

    if (error || !data) return null;

    // Fetch subscription details
    const { data: subData } = await supabase
      .from('user_subscriptions')
      .select(`*, subscription_plans (*)`)
      .eq('id', subscriptionId)
      .single();

    if (!subData) return null;

    const subscriptionWithPlan = subData as unknown as UserSubscriptionWithPlan;
    if (!subscriptionWithPlan.subscription_plans) return null;

    return {
      ...data,
      subscription: subscriptionWithPlan,
      plan: subscriptionWithPlan.subscription_plans,
    } as SubscriptionUsageWithDetails;
  }

  // Otherwise, fetch ALL active subscriptions and aggregate
  const subscriptions = await getAllActiveSubscriptions(userId);
  if (subscriptions.length === 0) return null;

  const subIds = subscriptions.map(s => s.id);
  
  const { data: usages, error } = await supabase
    .from('subscription_usage')
    .select('*')
    .eq('user_id', userId)
    .in('subscription_id', subIds)
    .lte('period_start', now)
    .gt('period_end', now);

  if (error) {
    logger.error('Error fetching subscription usages:', error);
    return null;
  }

  if (!usages || usages.length === 0) return null;

  // Aggregate credits
  let totalRemaining = 0;
  let totalUsed = 0;
  let totalIncluded = 0;
  let totalOverage = 0;

  usages.forEach(u => {
    totalRemaining += u.credits_remaining;
    totalUsed += u.credits_used;
    totalIncluded += u.credits_included;
    totalOverage += u.overage_credits;
  });

  // Use the most recent subscription as the primary one for display
  const primarySub = subscriptions[0];
  const primaryUsage = usages.find(u => u.subscription_id === primarySub.id) || usages[0];

  return {
    ...primaryUsage,
    credits_remaining: totalRemaining,
    credits_used: totalUsed,
    credits_included: totalIncluded,
    overage_credits: totalOverage,
    subscription: primarySub as unknown as UserSubscription,
    plan: primarySub.subscription_plans as SubscriptionPlan,
  } as SubscriptionUsageWithDetails;
}

/**
 * Fetches subscription invoices for a user
 * @param userId - The user ID to fetch invoices for
 * @param limit - Maximum number of invoices to return (default: 10)
 * @returns Array of invoices ordered by invoice_date descending
 */
export async function getSubscriptionInvoices(
  userId: string,
  limit: number = 10
): Promise<SubscriptionInvoice[]> {
  const { data, error } = await supabase
    .from('subscription_invoices')
    .select('*')
    .eq('user_id', userId)
    .order('invoice_date', { ascending: false })
    .limit(limit);

  if (error) {
    logger.error('Error fetching invoices:', error);
    return [];
  }

  return (data || []) as SubscriptionInvoice[];
}

/**
 * Fetches all available subscription plans
 * @returns Array of active subscription plans ordered by display_order
 */
export async function getAvailablePlans(): Promise<SubscriptionPlan[]> {
  const { data, error } = await supabase
    .from('subscription_plans')
    .select('*')
    .eq('is_active', true)
    .order('display_order', { ascending: true });

  if (error) {
    logger.error('Error fetching plans:', error);
    return [];
  }

  return (data || []) as SubscriptionPlan[];
}
